import React, { useEffect, useState } from "react";
import {
  Upload,
  ArrowRight,
  FileText,
  Target,
  Clock,
  Zap,
  AlertCircle,
  ChevronRight,
  Check,
} from "lucide-react";
import FileUploadForm from "@/components/Forms/FileUploadForm";
import JustFileUploadForm from "@/components/Forms/JustFileUploadForm";

const Card = ({ children, className }) => (
  <div
    className={`bg-gray-900/50 backdrop-blur-xl border border-gray-800/60 rounded-xl hover:border-violet-500/30 transition-all duration-300 ${className}`}
  >
    {children}
  </div>
);

const MockInterviewPage = () => {
  const [file, setFile] = useState(null);
  const stats = [
    { value: "500+", label: "Templates Delivered" },
    { value: "4.9", label: "Average Rating" },
    { value: "2 Days", label: "Delivery Time" },
  ];

  const features = [
    {
      icon: FileText,
      title: "ATS-Optimized Review",
      description:
        "Get your resume optimized for Applicant Tracking Systems with our expert analysis.",
      color: "from-blue-400 to-blue-600",
    },
    {
      icon: Target,
      title: "Industry-Specific Feedback",
      description:
        "Receive tailored suggestions based on your target industry and role.",
      color: "from-purple-400 to-purple-600",
    },
    {
      icon: Clock,
      title: "24-Hour Turnaround",
      description:
        "Quick delivery without compromising on quality and attention to detail.",
      color: "from-pink-400 to-pink-600",
    },
    {
      icon: Zap,
      title: "Priority Support",
      description:
        "Direct access to our experts for follow-up questions and revisions.",
      color: "from-orange-400 to-orange-600",
    },
  ];

  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className="min-h-screen md:-mt-12 md:mb-32 bg-gray-950 text-white">
      {/* Hero Section */}
      <div className="fixed inset-0 -z-10 bg-[linear-gradient(to_bottom,rgba(0,0,0,0)_0%,rgba(0,0,0,0.8)_100%)]">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSA2MCAwIEwgMCAwIDAgNjAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSg5OSwgMTAyLCAyNDEsIDAuMSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>
      </div>
      <div
        className={`relative group transition-all duration-1000 delay-300 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        <div className="px-4 sm:px-6 lg:px-8  grid lg:grid-cols-2 gap-12 max-w-7xl mx-auto pt-24 lg:pt-40">
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 text-purple-400 text-sm">
              <div className="w-2 h-2 rounded-full bg-purple-400 animate-pulse"></div>
              Premium Offer - Limited Time!
            </div>

            <h1 className="text-3xl lg:text-5xl font-bold leading-tight">
              Got A Real Interview
              <br />
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                Coming Up?
              </span>
            </h1>

            <p className="text-gray-400 text-lg leading-relaxed">
              Don’t let your dream job become a{" "}
              <em className="text-indigo-400 not-italic">dream</em>. If you’ve
              got a real interview lined up, we’ll grill you before they do —
              mock style (but serious).
            </p>

            <div className="bg-indigo-950/50 border-l-4 border-indigo-500 p-5 rounded-r-lg transform hover:scale-102 transition-transform">
              <p className="text-indigo-300 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                <span>
                  We only take mock interviews for real interviews. No mockery
                  for the sake of it.
                </span>
              </p>
            </div>

            <div
              className="bg-black border-2 border-gray-800 rounded-xl p-6 transform hover:scale-102 transition-transform duration-300 hover:border-indigo-800/50 hover:shadow-lg hover:shadow-indigo-900/10 group"
              // style={getContentAnimationStyle(5)}
            >
              <div className="flex items-start">
                <div className="mr-4">
                  <div className="h-14 w-14 bg-indigo-600/20 rounded-xl flex items-center justify-center group-hover:bg-indigo-600/30 transition-colors">
                    <span className="text-indigo-400 text-2xl font-bold">
                      ₹
                    </span>
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-xl mb-2 text-white flex items-center">
                    1-hour Mock Interview
                    <ChevronRight className="h-4 w-4 mx-1 text-indigo-500" />
                    <span className="text-indigo-400">₹499 only</span>
                  </h3>
                  <p className="text-gray-400">
                    You get feedback, tips, and some tough love.
                  </p>
                </div>
              </div>
            </div>

            <div
              className="border-t border-gray-800 pt-6 space-y-4"
              // style={getContentAnimationStyle(6)}
            >
              <div className="flex items-start gap-3">
                <div className="h-6 w-6 rounded-full bg-indigo-900/50 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="h-3.5 w-3.5 text-indigo-400" />
                </div>
                <p className="text-gray-400">
                  We'll review your job description & resume.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="h-6 w-6 rounded-full bg-indigo-900/50 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Clock className="h-3.5 w-3.5 text-indigo-400" />
                </div>
                <p className="text-gray-400">
                  If we think we're the right fit to take your mock, we'll get
                  back within 24 hours.
                </p>
              </div>
            </div>

            {/* <div className="space-y-8">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight">
                Our Professional
                <br />
                <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                  Review Process
                </span>
              </h2>

              <div className="space-y-6">
                {[
                  {
                    title: "Submit Your Resume",
                    description:
                      "Upload your current resume and share your career goals.",
                  },
                  {
                    title: "Expert Review",
                    description:
                      "Our industry experts analyze every aspect of your resume.",
                  },
                  {
                    title: "Detailed Feedback",
                    description:
                      "Receive comprehensive feedback and suggestions.",
                  },
                  {
                    title: "Implementation Support",
                    description:
                      "Get guidance on implementing the recommended changes.",
                  },
                ].map((step, index) => (
                  <div key={index} className="flex gap-4 items-start">
                    <div className="w-8 h-8 rounded-full bg-purple-400/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-purple-400 font-semibold">
                        {index + 1}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1">
                        {step.title}
                      </h3>
                      <p className="text-gray-400">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div> */}

            {/* <div className="grid grid-cols-3 gap-6">
                {stats.map((stat, index) => (
                  <Card key={index} className="p-4 text-center group">
                    <div className="text-2xl font-bold bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent group-hover:scale-110 transition-transform">
                      {stat.value}
                    </div>
                    <div className="text-sm text-gray-400">{stat.label}</div>
                  </Card>
                ))}
              </div> */}
          </div>
          <JustFileUploadForm />
        </div>
      </div>

      {/* Premium Features Section */}
      {/* <div className="py-16 sm:py-32 ">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-12">
            Premium Features
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`p-8 rounded-xl bg-gradient-to-r ${feature.color} text-white shadow-lg`}
              >
                <feature.icon className="w-12 h-12 mb-4" />
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default MockInterviewPage;
